import React, { useState, useEffect } from 'react'
import {
    Modal,
    Box,
    Typography,
    TextField,
    Slider,
    Button,
    CircularProgress,
    Stack
} from '@mui/material'

type ParaphraseModalProps = {
    open: boolean
    originalClause: string
    isLoading: boolean
    newClause: string
    newGeneratedClause: string
    onClose: (updatedClause: string | null) => void
    onClauseFavorabilityChange: (diff: number | null) => void
    steps: {
        steps: number
        min: number
        max: number
    }
}

const style = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: { xs: 2, sm: 4 },
    width: { xs: 'calc(100% - 32px)', sm: 600 },
    maxWidth: 'calc(100% - 32px)',
    maxHeight: 'calc(100dvh - 32px)',
    boxSizing: 'border-box',
    overflowY: 'auto'
}

const ParaphraseModal: React.FC<ParaphraseModalProps> = ({
    open,
    originalClause,
    newGeneratedClause,
    onClose,
    isLoading,
    steps,
    onClauseFavorabilityChange
}) => {
    const [editedClause, setEditedClause] = useState(originalClause)
    const [sliderValue, setSliderValue] = useState<number>(null)

    useEffect(
        () => {
            if (open) {
                setEditedClause('')
                setSliderValue(null)
            }
        },
        [open, originalClause]
    )

    useEffect(
        () => {
            if (!open) {
                setEditedClause('')
                return
            }
            //alert(newGeneratedClause)
            setEditedClause(newGeneratedClause)
        },
        [open, newGeneratedClause]
    ) // sync whenever prop changes

    const handleSave = () => {
        onClose(editedClause)

        setEditedClause('')
    }

    const handleCancel = () => {
        onClose(null)
        setEditedClause('')
    }

    const handleSliderChange = (_event, newValue) => {
        onClauseFavorabilityChange(newValue)
        setSliderValue(newValue)
    }

    return (
        <Modal open={open} onClose={handleCancel}>
            <Box sx={style}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6">Modify Clause</Typography>
                    <Button size="small" onClick={handleCancel}>
                        Close
                    </Button>
                </Stack>

                <Typography sx={{ mt: 2 }}>Original Clause:</Typography>
                <Typography sx={{ mt: 1, mb: 2, fontStyle: 'italic' }}>
                    {originalClause}
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Clause Modification"
                    disabled={isLoading}
                    value={editedClause}
                    onChange={e => setEditedClause(e.target.value)}
                />

                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none' // lets user still type
                        }}
                    >
                        <CircularProgress size={72} />
                    </Box>
                )}

                <Typography sx={{ mt: 3 }}>Favorability Adjustment</Typography>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={{ xs: 1, sm: 2 }}
                >
                    <Typography variant="body2" sx={{ minWidth: { sm: 90 } }}>
                        Less Favorable
                    </Typography>
                    <Slider
                        value={sliderValue}
                        onChange={handleSliderChange}
                        min={steps.min}
                        max={steps.max}
                        marks
                        sx={{ flexGrow: 1 }}
                    />
                    <Typography
                        variant="body2"
                        sx={{ minWidth: { sm: 90 }, textAlign: { sm: 'right' } }}
                    >
                        More Favorable
                    </Typography>
                </Stack>

                <Stack
                    direction={{ xs: 'column-reverse', sm: 'row' }}
                    spacing={2}
                    justifyContent="flex-end"
                    sx={{ mt: 4 }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Save
                    </Button>
                </Stack>
            </Box>
        </Modal>
    )
}

export default ParaphraseModal
